import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl } from '../utils/api';

function AppointmentReviewForm({ appointment, existingReview, config, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (existingReview) return <div className="review-status"><span className="badge badge-completed">Review {existingReview.status}</span>{existingReview.status === 'pending' && <small>Your review is awaiting moderation.</small>}</div>;
  if (appointment.status !== 'completed') return null;
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try { await axios.post(apiUrl(`/api/appointments/${appointment._id}/review`), { rating, title, comment }, config); onSubmitted(); }
    catch (err) { setError(err?.response?.data?.message || 'Could not submit review.'); }
    finally { setBusy(false); }
  };
  return <form className="review-form" onSubmit={submit}>
    <div className="review-form-heading"><strong>Share your experience</strong><span className="verified-review-badge">Completed booking</span></div>
    <label>Rating<select value={rating} onChange={(event) => setRating(Number(event.target.value))}>{[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value} stars</option>)}</select></label>
    <label>Title <input value={title} maxLength="120" placeholder="Optional headline" onChange={(event) => setTitle(event.target.value)} /></label>
    <label>Your review <textarea required minLength="3" maxLength="2000" value={comment} placeholder="Tell other customers about your experience" onChange={(event) => setComment(event.target.value)} /></label>
    {error && <p className="error-message">{error}</p>}
    <button className="button primary" disabled={busy}>{busy ? 'Submitting…' : 'Submit review'}</button>
  </form>;
}

function RescheduleRequestForm({ appointment, config, onSubmitted }) {
  const [date, setDate] = useState(''); const [time, setTime] = useState(''); const [notes, setNotes] = useState(''); const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const submit = async (event) => { event.preventDefault(); setBusy(true); setError(''); try { await axios.post(apiUrl(`/api/appointments/${appointment._id}/reschedule-request`), { date, time, notes }, config); onSubmitted(); } catch (err) { setError(err?.response?.data?.message || 'Could not request a reschedule.'); } finally { setBusy(false); } };
  return <form className="reschedule-request-form" onSubmit={submit}><strong>Request a different day or time</strong><label>Requested date<input type="date" required min={new Date().toISOString().slice(0, 10)} value={date} onChange={(event) => setDate(event.target.value)} /></label><label>Requested time<input type="time" required value={time} onChange={(event) => setTime(event.target.value)} /></label><label>Message to the business (optional)<textarea value={notes} maxLength="1000" onChange={(event) => setNotes(event.target.value)} placeholder="Tell the business what works better for you" /></label>{error && <p className="error-message">{error}</p>}<button className="button primary" disabled={busy}>{busy ? 'Sending request…' : 'Send reschedule request'}</button></form>;
}

function Dashboard() {
  const getInitials = (value) => {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'U';
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  };

  const statusLabel = (value = 'unknown') => {
    const label = value.replaceAll('_', ' ').toUpperCase();
    switch (value) {
      case 'requested': return `🟠 ${label}`;
      case 'pending': return `🔵 ${label}`;
      case 'confirmed': return `🟢 ${label}`;
      case 'reschedule_requested': return `🟣 ${label}`;
      case 'cancelled': return `⚪ ${label}`;
      case 'completed': return `✅ ${label}`;
      case 'no_show': return `⚫ ${label}`;
      default: return label;
    }
  };

  const statusBadgeStyle = (status = '') => {
    switch (status) {
      case 'requested':
        return { background: '#fff1e6', color: '#9a3412', borderColor: '#f97316', boxShadow: '0 0 0 1px rgba(249,115,22,0.18) inset' };
      case 'pending':
        return { background: '#eaf2ff', color: '#1d4ed8', borderColor: '#3b82f6', boxShadow: '0 0 0 1px rgba(59,130,246,0.18) inset' };
      case 'confirmed':
        return { background: '#e8fff0', color: '#166534', borderColor: '#22c55e', boxShadow: '0 0 0 1px rgba(34,197,94,0.18) inset' };
      case 'reschedule_requested':
        return { background: '#f5f3ff', color: '#6d28d9', borderColor: '#a78bfa', boxShadow: '0 0 0 1px rgba(167,139,250,0.18) inset' };
      case 'cancelled':
        return { background: '#f8fafc', color: '#475569', borderColor: '#cbd5e1', boxShadow: '0 0 0 1px rgba(148,163,184,0.16) inset' };
      case 'completed':
        return { background: '#ecfeff', color: '#0f766e', borderColor: '#2dd4bf', boxShadow: '0 0 0 1px rgba(45,212,191,0.18) inset' };
      case 'no_show':
        return { background: '#111827', color: '#f9fafb', borderColor: '#374151', boxShadow: '0 0 0 1px rgba(55,65,81,0.35) inset' };
      default:
        return {};
    }
  };

  const [user, setUser] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (!userInfo?.token) {
          navigate('/login');
          return;
        }

        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        const [profileRes, appointmentsRes, reviewsRes] = await Promise.all([
          axios.get(apiUrl('/api/auth/profile'), config),
          axios.get(apiUrl('/api/appointments'), config),
          axios.get(apiUrl('/api/reviews/mine'), config),
        ]);
        setUser(profileRes.data);
        setAppointments(appointmentsRes.data);
        setReviews(reviewsRes.data || []);
      } catch (error) {
        console.error(error);
        navigate('/login');
      }
    };

    fetchDashboard();
  }, [navigate]);

  const filteredAppointments = useMemo(() => {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const toLocalKey = (value) => {
      const date = value ? new Date(value) : null;
      if (!date || Number.isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    const formatDateTime = (appointment) => {
      const dateLabel = appointment?.date ? new Date(`${appointment.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date';
      const timeLabel = appointment?.time ? (() => {
        const timeDate = new Date(`1970-01-01T${appointment.time}`);
        return Number.isNaN(timeDate.getTime()) ? appointment.time : timeDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      })() : '';
      return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
    };

    return appointments.filter((appointment) => {
      const term = search.trim().toLowerCase();
      if (term) {
        const haystack = [appointment.status, appointment.service, appointment.business?.name, appointment.staffProfile?.displayName, appointment.staffName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (statusFilter !== 'all' && appointment.status !== statusFilter) return false;

      const key = toLocalKey(appointment.date);
      if (quickFilter !== 'all') {
        if (!key) return false;
        const diff = Math.round((new Date(`${key}T00:00:00`).getTime() - new Date(`${todayKey}T00:00:00`).getTime()) / 86400000);
        if (quickFilter === 'today' && diff !== 0) return false;
        if (quickFilter === 'upcoming' && diff < 0) return false;
        if (quickFilter === 'past' && diff >= 0) return false;
      }
      appointment._formattedDateTime = formatDateTime(appointment);
      return true;
    });
  }, [appointments, quickFilter, search, statusFilter]);

  const reviewsByAppointment = new Map(reviews.map((review) => [String(review.appointment?._id || review.appointment), review]));
  const refreshReviews = async () => { const current = JSON.parse(localStorage.getItem('userInfo') || 'null'); if (current?.token) { const response = await axios.get(apiUrl('/api/reviews/mine'), { headers: { Authorization: `Bearer ${current.token}` } }); setReviews(response.data || []); } };

  if (!user) return <div className="page"><div className="panel">Loading...</div></div>;

  return (
    <div className="page dashboard-page">
      <section className="split-page">
        <div className="hero-copy panel">
          <p className="eyebrow">My Appointments</p>
          <div className="profile-hero-row">
            <div className="avatar-badge">{getInitials(user.name)}</div>
            <div>
              <h1>Welcome, {user.name}</h1>
              <p className="muted-text">Role: {user.role}</p>
            </div>
          </div>
          <div className="profile-summary-row">
            <span className="badge">{user.email}</span>
            <span className="badge">{user.role}</span>
          </div>
        </div>

        <div className="stacked-panels">
          <div className="panel">
            <h2>Quick actions</h2>
            <div className="hero-actions hero-actions-wrap">
              <Link className="button primary" to="/appointment">Book Appointment</Link>
              <Link className="button ghost" to="/profile">Update profile</Link>
              <Link className="button ghost" to="/change-password">Change password</Link>
              <Link className="button ghost" to="/forgot-password">Email reset</Link>
              <details className="more-actions">
                <summary className="button ghost more-actions-summary">More</summary>
                <div className="more-actions-menu">
                  {['admin', 'owner', 'manager'].includes(user.role) ? (
                    <>
                      <Link className="button ghost compact" to="/owner">Owner Dashboard</Link>
                      {user.role === 'admin' && <Link className="button ghost compact" to="/admin">Admin Panel</Link>}
                    </>
                  ) : (
                    <span className="more-actions-note">No extra actions for this account.</span>
                  )}
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      <section className="section-header"><p className="eyebrow">Bookings</p><h2>Your appointment requests</h2></section>
      <section className="panel">
        <div className="owner-appointment-filters">
          <label>
            Search
            <input type="text" placeholder="Business, service, staff, or status" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <label>
            Quick filter
            <select value={quickFilter} onChange={(e) => setQuickFilter(e.target.value)}>
              <option value="all">All appointments</option>
              <option value="today">Today</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </label>
          <label>
            Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              {['requested', 'pending', 'confirmed', 'reschedule_requested', 'cancelled', 'completed', 'no_show'].map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
          <button type="button" className="ghost" onClick={() => { setSearch(''); setQuickFilter('all'); setStatusFilter('all'); }}>Clear filters</button>
        </div>
        <p className="section-note">Showing {filteredAppointments.length} of {appointments.length} appointments.</p>
      </section>

      <section className="customer-appointments-list">
        {appointments.length === 0 && <div className="panel"><p>No appointments yet.</p></div>}
        {filteredAppointments.length === 0 && appointments.length > 0 && <div className="panel"><p>No appointments match the current filters.</p></div>}
        {filteredAppointments.map((appointment) => (
          <article className="appointment-card" key={appointment._id}>
            <div className="appointment-card-head">
              <div className="appointment-card-business">
                <div className="appointment-card-business-line">
                  <div className="business-type">{appointment.business?.name || 'Business'}</div>
                  <span className={`badge badge-${appointment.status}`} style={statusBadgeStyle(appointment.status)}>{statusLabel(appointment.status)}</span>
                </div>
                <h3>{appointment.service}</h3>
                <p className="appointment-professional-line">with {appointment.staffProfile?.displayName || appointment.staffName || 'Any available professional'}</p>
              </div>
            </div>
            <div className="appointment-card-primary-details">
              <div><span className="appointment-card-label">Date</span><strong>{new Date(appointment.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</strong><span className="appointment-card-label">Time</span><strong className="appointment-time-large">{appointment.time}</strong></div>
              <div><span className="appointment-card-label">Reference</span><strong>{appointment._id.slice(-6).toUpperCase()}</strong></div>
            </div>
            {appointment.notes ? <div className="appointment-notes"><span className="appointment-card-label">Your note</span><p>{appointment.notes}</p></div> : null}
            {appointment.status === 'reschedule_requested' ? <p className="permission-note">Your reschedule request is waiting for the business to review.</p> : !['cancelled', 'completed', 'no_show'].includes(appointment.status) && <details className="appointment-extra-details"><summary>Request a different day or time</summary><RescheduleRequestForm appointment={appointment} config={{ headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem('userInfo') || '{}').token}` } }} onSubmitted={() => window.location.reload()} /></details>}
            {appointment.business?.subscriptionPlan === 'starter' && <p className="permission-note">WhatsApp updates are unavailable for Starter businesses. Email and app notifications remain available.</p>}
            {appointment.status === 'completed' && <details className="appointment-extra-details"><summary>Leave a review</summary><AppointmentReviewForm appointment={appointment} existingReview={reviewsByAppointment.get(String(appointment._id))} config={{ headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem('userInfo') || '{}').token}` } }} onSubmitted={refreshReviews} /></details>}
          </article>
        ))}
      </section>
    </div>
  );
}

export default Dashboard;
